import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Generates a unique campaign ID for existing campaigns during migration
 * Format: Wiin_CleanedTitle_Timestamp_Random3Digits
 */
function generateUniqueCampaignId(title: string): string {
  const cleanTitle = title
    .replace(/[^a-zA-Z0-9æøåÆØÅ]/g, '_')
    .replace(/_+/g, '_')
    .substring(0, 30)
    .replace(/^_|_$/g, '');
  
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  
  return `Wiin_${cleanTitle}_${timestamp}_${random}`;
}

/**
 * Migration script to add campaignId field to existing campaigns
 * Run this ONCE to migrate your existing campaigns
 * 
 * Usage:
 * 1. Open browser console on any page with Firebase loaded
 * 2. Import this function
 * 3. Call migrateCampaignIds()
 */
export async function migrateCampaignIds() {
  try {
    console.log('🔄 Starting campaign ID migration...');
    console.log('========================================');
    
    const campaignsRef = collection(db, 'campaigns');
    const snapshot = await getDocs(campaignsRef);
    
    console.log(`📊 Found ${snapshot.size} campaigns to check`);
    
    let updated = 0;
    let skipped = 0;
    
    for (const docSnapshot of snapshot.docs) {
      const data = docSnapshot.data();
      
      // Skip if campaignId already exists
      if (data.campaignId) {
        console.log(`⏭️  Skipping: ${data.title || docSnapshot.id} - already has campaignId: ${data.campaignId}`);
        skipped++;
        continue;
      }
      
      // Generate new unique campaignId
      const campaignId = generateUniqueCampaignId(data.title || 'Campaign');
      
      // Update the document
      await updateDoc(doc(db, 'campaigns', docSnapshot.id), {
        campaignId: campaignId
      });
      
      console.log(`✅ Updated: ${data.title || docSnapshot.id}`);
      console.log(`   Firestore Doc ID: ${docSnapshot.id}`);
      console.log(`   New campaignId: ${campaignId}`);
      console.log('---');
      
      updated++;
      
      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    console.log('========================================');
    console.log(`🎉 Migration complete!`);
    console.log(`✅ Updated: ${updated} campaigns`);
    console.log(`⏭️  Skipped: ${skipped} campaigns (already had campaignId)`);
    console.log(`📊 Total: ${snapshot.size} campaigns`);
    
  } catch (error) {
    console.error('❌ Migration failed:', error);
    throw error;
  }
}

// Uncomment the line below to run migration (use with caution)
// migrateCampaignIds();

